import { AgeCheckConfig, ProcessVisitorResponse } from '../../types'
import {
    AgeVerificationProvider,
    AgeVerificationState,
} from '../../types/state'
import { appAction } from '../actions/workerActions'
import { COOKIE_USER_ID, COOKIE_USER_LEVEL } from '../constants'
import {
    AppAction,
    AppStateType,
    GatedDisplayModes,
    SecureStateType,
} from '../constants/enums'
import { updateAppState } from '../state/state.app'
import { updateSecureState } from '../state/state.secure'

import { setCookie } from '../utilities/cookies'
import { safeObj } from '../utilities/safe'
import { getAgeCheckState, isAgeCheckRequired } from './ageCheck'
import { fetchEntryInformation } from './api/account'
import { decideACR } from './videoPlayerReady'

// without a visitor lookup the age gate stays off, as everywhere else that
// reads ageGateActive
const NOT_AGE_GATED = { acr: false, noAudio: false } as const

/** Fetch visitor information and update state */
export const getVisitorInformation = async (result: ProcessVisitorResponse) => {
    if (!result || typeof result?.age_check_required === 'undefined') {
        try {
            const accountDetails = await fetchEntryInformation()
            const { result: processVisitorResult } = safeObj(accountDetails)

            if (!processVisitorResult) {
                decideACR(NOT_AGE_GATED)
                return
            }

            result = processVisitorResult
        } catch (error) {
            console.error(error)
            decideACR(NOT_AGE_GATED)
            return
        }
    }

    const {
        user,
        age_check_config,
        age_check_required,
        age_check_services,
        cookies,
        maintenance_mode,
        redirection,
    } = result

    const level = user?.access_level || 0
    const token = user?.token
    const id = user?.id || 0

    setCookie(COOKIE_USER_LEVEL, level + '')
    setCookie(COOKIE_USER_ID, id + '')

    // dynamically handle age verification providers
    const providers = Object.keys(age_check_services || {})
    const serviceProviders: Record<string, AgeVerificationProvider> = {}
    providers.forEach((provider) => {
        serviceProviders[provider] = {
            perks: age_check_services[provider]?.perks,
            enabled: age_check_services[provider]?.enabled === 1,
            description: age_check_services[provider]?.description,
            url: age_check_services[provider]?.url,
            region: age_check_services[provider]?.region,
            countryCode: age_check_services[provider]?.country_code,
            graphicHtml: age_check_services[provider]?.graphic_html,
            logoPath: age_check_services[provider]?.logo_img_path,
        }
    })

    // yoti is allowed by default for now; however ondato is conditional
    if (providers.length === 0) {
        serviceProviders['yoti'] = {
            enabled: true,
            url: 'https://bouncer.myfreecams.com/',
        }
    }

    if (redirection && redirection.startsWith('https://')) {
        // if a user is forcibly redirected from MFC App
        window.location.replace(redirection)
    }

    // update cookies
    if (cookies && Array.isArray(cookies) && cookies?.length > 0) {
        cookies.forEach((cookieData) => {
            const cookieKey = cookieData[0].toString()
            const cookieValue = cookieData[1].toString()
            const expiration = new Date((cookieData[2] as number) * 1000)
            setCookie(cookieKey, cookieValue, {
                expires: expiration,
            })
        })
    }

    // age check configs
    const ageCheckRequired = isAgeCheckRequired(age_check_required)
    const secureAgeVerifificationState: AgeVerificationState = {
        ...(ageCheckRequired
            ? {
                  ageVerificationProviders: serviceProviders,
                  ageGateActive: true,
                  legalRegion: result.legal_region,
              }
            : { ageGateActive: false }),
    }
    const secureDisplayType = {
        settings: getAgeGateDisplayType(age_check_config),
        noVideo: getNoVideoValue(age_check_config),
    }
    updateSecureState(
        SecureStateType.AGE_VERIFICATION,
        secureAgeVerifificationState,
    )
    if (ageCheckRequired) {
        updateSecureState(SecureStateType.DISPLAY_CONFIG, secureDisplayType)
    }
    // after the secure state, which the video code reads once the SDK class
    // is out
    decideACR({
        acr: ageCheckRequired,
        noAudio: !getWithVideoValue(age_check_config),
    })
    appAction(AppAction.SET_AGE_CHECK, getAgeCheckState(result))

    const appState = { maintenanceMode: maintenance_mode === 1 }

    updateAppState(AppStateType.APP, appState)

    const sessionState = { token }

    if (token) {
        updateAppState(AppStateType.SESSION, sessionState)
    }

    return {
        user: { level, id, ...(user ?? {}) },
        ...sessionState,
        ...secureAgeVerifificationState,
        ...secureDisplayType,
        ...appState,
    }
}

export const getAgeGateDisplayType = (ageCheckConfig: AgeCheckConfig) => {
    if (!ageCheckConfig || Array.isArray(ageCheckConfig))
        return GatedDisplayModes.hideEverything
    if (ageCheckConfig.acr_config) return GatedDisplayModes.hideEverything
    if (ageCheckConfig.site_preview) return GatedDisplayModes.preview
    return GatedDisplayModes.hideEverything
}

export const getNoVideoValue = (ageCheckConfig: AgeCheckConfig) => {
    if (!ageCheckConfig || Array.isArray(ageCheckConfig)) return false
    const { site_preview } = ageCheckConfig
    if (!site_preview || Array.isArray(site_preview)) return false
    return site_preview.no_video === true
}

/**
 * A site preview whose player keeps its live audio. www calls setACR() for
 * it and setACR({ no_audio: true }) otherwise (misc.MfcJs
 * MfcClientVideo.js); mobile's acrBlockAudio is `acr && !with_video`.
 */
export const getWithVideoValue = (ageCheckConfig: AgeCheckConfig) => {
    if (!ageCheckConfig || Array.isArray(ageCheckConfig)) return false
    const { site_preview } = ageCheckConfig
    if (!site_preview || Array.isArray(site_preview)) return false
    return site_preview.with_video === true
}
